/* eslint-disable react-hooks/refs --
 * False positives from dnd-kit's `useSortable` return shape: the rule
 * treats `sortable.setNodeRef`, `sortable.attributes`, `sortable.listeners`,
 * `sortable.transform`, and `sortable.isDragging` as ref accesses during
 * render. They are not React refs — they're plain return-value props
 * meant to be spread/forwarded into JSX, which is dnd-kit's documented
 * usage pattern.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

// Sprint 22 hot-fix (2026-05-08) — column-reorder mode prefixes the
// drag id so handleDragEnd can route between card-drop and column-
// reorder logic. Card ids are intake UUIDs; column drag ids are
// `STAGE_HANDLE_PREFIX${stageId}`.
const STAGE_HANDLE_PREFIX = 'stage-handle:'

// Horizontal scroll-position persistence (see scrollRestorationKey below).
const SCROLL_KEY_PREFIX = 'spi-ops:kanban-scroll:'

export interface KanbanStage {
  id: string
  label: string
  /** Stage-level accent — neutral/warning/success by convention. */
  accent?: 'neutral' | 'success' | 'warning' | 'danger'
}

export interface KanbanItem {
  id: string
  stageId: string
}

export interface KanbanBoardProps<T extends KanbanItem> {
  stages: KanbanStage[]
  items: T[]
  renderCard: (item: T) => ReactNode
  /** Called when a card is dropped on a different stage. Only invoked when
   *  `allowedTransitions?.(from, to) !== false`. */
  onAdvance: (item: T, from: string, to: string) => void
  /** Restrict legal transitions. Default: all transitions allowed. */
  allowedTransitions?: (from: string, to: string) => boolean
  /** Click a card (non-drag). Open the detail drawer upstream. */
  onSelect?: (item: T) => void
  selectedId?: string
  /** Which stages are collapsed to the 48px rail. State lives in the parent
   *  so the shell can persist it (spec §3.3). */
  collapsedStages?: ReadonlySet<string>
  onToggleStage?: (stageId: string) => void
  /**
   * Sprint 22 hot-fix — when provided, columns become drag-reorderable
   * via a hover-revealed grip handle on the column header. Activator
   * is the grip only — the rest of the header stays click-only so
   * collapse/expand isn't hijacked. Cards keep their existing drag.
   */
  onStageReorder?: (next: string[]) => void
  className?: string
  /**
   * Opt-in horizontal scroll-position persistence for the board's outer
   * stage-column row, keyed in sessionStorage. Pass a distinct, stable
   * string per board instance (e.g. 'sales-inbox'). Only needed when
   * `onSelect` navigates to a route that unmounts this component —
   * without it, returning via back-nav resets scrollLeft to 0. Omit for
   * boards where `onSelect` opens an in-place drawer/panel (the board
   * stays mounted, so scroll position survives naturally).
   */
  scrollRestorationKey?: string
}

/**
 * @dnd-kit-based kanban board. Spec §Archetype 2.
 *
 * Invalid transitions are rejected silently (no shake — that was the
 * reference CSS; we opt for no animation per Principle 11.5's "no
 * attention-seeking motion" unless needed). The `onAdvance` callback
 * simply doesn't fire.
 *
 * Keyboard nav (h/l/j/k/Enter/Shift+l/Shift+h) is wired at the page level
 * via useKeyboardShortcut once M5 lands — this primitive surfaces the
 * shape but doesn't own the shortcuts.
 */
export function KanbanBoard<T extends KanbanItem>({
  stages,
  items,
  renderCard,
  onAdvance,
  allowedTransitions,
  onSelect,
  selectedId,
  collapsedStages,
  onToggleStage,
  onStageReorder,
  className,
  scrollRestorationKey,
}: KanbanBoardProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )
  const [activeId, setActiveId] = useState<string | null>(null)
  const activeItem = activeId ? items.find((i) => i.id === activeId) : null

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const scrollSaveFrameRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!scrollRestorationKey || !el) return

    const restore = () => {
      let saved: string | null = null
      try {
        saved = sessionStorage.getItem(SCROLL_KEY_PREFIX + scrollRestorationKey)
      } catch {
        return
      }
      if (saved == null) return
      const parsed = Number(saved)
      if (Number.isNaN(parsed)) return
      const max = el.scrollWidth - el.clientWidth
      el.scrollLeft = Math.max(0, Math.min(parsed, max))
    }

    restore()
    // Defensive follow-up for late reflow (webfont swap, scrollbar gutter)
    // that could shift scrollWidth a frame after the initial layout pass.
    const frame = requestAnimationFrame(restore)
    return () => cancelAnimationFrame(frame)
  }, [scrollRestorationKey])

  useLayoutEffect(() => {
    return () => {
      if (scrollSaveFrameRef.current != null) {
        cancelAnimationFrame(scrollSaveFrameRef.current)
      }
    }
  }, [])

  const handleScroll = scrollRestorationKey
    ? () => {
        const el = scrollRef.current
        if (!el) return
        if (scrollSaveFrameRef.current != null) {
          cancelAnimationFrame(scrollSaveFrameRef.current)
        }
        scrollSaveFrameRef.current = requestAnimationFrame(() => {
          try {
            sessionStorage.setItem(
              SCROLL_KEY_PREFIX + scrollRestorationKey,
              String(el.scrollLeft),
            )
          } catch {
            // sessionStorage unavailable (locked-down embed context) — no-op.
          }
        })
      }
    : undefined

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id))

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null)
    const activeIdStr = String(e.active.id)
    const overIdStr = e.over ? String(e.over.id) : null
    if (!overIdStr) return

    // Sprint 22 hot-fix — column reorder path. Stage handles (the
    // grip on the column header) prefix their id; route those to
    // onStageReorder before checking the card-drop logic.
    if (activeIdStr.startsWith(STAGE_HANDLE_PREFIX) && onStageReorder) {
      const fromStage = activeIdStr.slice(STAGE_HANDLE_PREFIX.length)
      const toStage = overIdStr.startsWith(STAGE_HANDLE_PREFIX)
        ? overIdStr.slice(STAGE_HANDLE_PREFIX.length)
        : null
      if (!toStage || toStage === fromStage) return
      const stageIds = stages.map((s) => s.id)
      const oldIndex = stageIds.indexOf(fromStage)
      const newIndex = stageIds.indexOf(toStage)
      if (oldIndex < 0 || newIndex < 0) return
      const next = [...stageIds]
      const [moved] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, moved)
      onStageReorder(next)
      return
    }

    // Card-drop path (existing behavior).
    const item = items.find((i) => i.id === activeIdStr)
    if (!item) return
    // Accept drops on the body droppable (stage:xxx) OR the column's sortable
    // outer div (stage-handle:xxx). The header area of a column only has the
    // sortable registered, so without this fallback a drop on an empty column's
    // header is silently rejected. The column-reorder guard above already
    // ensures stage-handle active items never reach here.
    const targetStage = overIdStr.startsWith('stage:')
      ? overIdStr.slice(6)
      : overIdStr.startsWith(STAGE_HANDLE_PREFIX)
        ? overIdStr.slice(STAGE_HANDLE_PREFIX.length)
        : null
    if (!targetStage || targetStage === item.stageId) return
    if (allowedTransitions && !allowedTransitions(item.stageId, targetStage)) return
    onAdvance(item, item.stageId, targetStage)
  }

  const countByStage = (id: string) => items.filter((i) => i.stageId === id).length
  const itemsInStage = (id: string) => items.filter((i) => i.stageId === id)
  const stageHandleIds = stages.map((s) => `${STAGE_HANDLE_PREFIX}${s.id}`)
  const reorderEnabled = !!onStageReorder

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <SortableContext
        items={stageHandleIds}
        strategy={horizontalListSortingStrategy}
      >
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          role="list"
          aria-label="Pipeline"
          className={cn(
            // h-full + min-h-0 lets the board fill the parent's height
            // so columns can be height-constrained (max-h-full inside)
            // and scroll their cards internally rather than growing the
            // page. Without h-full/min-h-0 the column max-h-full had
            // nothing to compute against and columns grew unbounded.
            'flex h-full min-h-0 gap-3 overflow-x-auto rounded-md bg-canvas p-4',
            className,
          )}
        >
          {stages.map((stage) => (
            <StageColumn
              key={stage.id}
              stage={stage}
              count={countByStage(stage.id)}
              collapsed={collapsedStages?.has(stage.id) ?? false}
              onToggle={onToggleStage ? () => onToggleStage(stage.id) : undefined}
              reorderEnabled={reorderEnabled}
            >
              {itemsInStage(stage.id).map((item) => (
                <Card
                  key={item.id}
                  item={item}
                  selected={selectedId === item.id}
                  onSelect={onSelect}
                >
                  {renderCard(item)}
                </Card>
              ))}
            </StageColumn>
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <div className="cursor-grabbing rounded-sm border border-border-default bg-surface p-2.5 shadow-md">
            {renderCard(activeItem)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

function StageColumn({
  stage,
  count,
  collapsed,
  onToggle,
  reorderEnabled,
  children,
}: {
  stage: KanbanStage
  count: number
  collapsed: boolean
  onToggle?: () => void
  reorderEnabled: boolean
  children: ReactNode
}) {
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `stage:${stage.id}`,
  })
  // Always call useSortable so hook order stays stable across renders.
  // Listeners are wired only to the grip handle below — clicks elsewhere
  // (header, body, badge) don't initiate a column drag.
  const sortable = useSortable({ id: `${STAGE_HANDLE_PREFIX}${stage.id}` })
  const sortableStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  }
  return (
    <div
      ref={sortable.setNodeRef}
      // Spread sortable.attributes FIRST so our explicit role/aria-label
      // override dnd-kit's defaults (which set role="button" — would
      // break the existing test contract that finds columns by listitem
      // role + accessible name).
      {...sortable.attributes}
      role="listitem"
      aria-label={`${stage.label} (${count})`}
      className={cn(
        // max-h-full bounds the column to the kanban board's height so
        // the inner card-list overflow-y-auto actually engages. Without
        // a height bound, the column grew tall and the page scrolled —
        // making the "sticky" header useless because nothing was
        // scrolling within the column.
        'group/col flex max-h-full min-h-[400px] flex-col rounded-md border border-border-subtle bg-surface-sunk',
        collapsed ? 'min-w-12 max-w-12' : 'w-70 min-w-70',
        isOver && 'border-accent bg-accent-subtle',
        sortable.isDragging && 'opacity-50',
      )}
      style={collapsed ? sortableStyle : { width: 280, minWidth: 280, ...sortableStyle }}
    >
      <header
        className={cn(
          // The header is OUTSIDE the inner overflow-y-auto card-list
          // so it stays put naturally as cards scroll within the
          // column. No `sticky` needed once the column is height-bounded.
          'flex items-center gap-2 border-b border-border-subtle bg-surface-sunk p-2.5',
          collapsed && 'flex-col gap-2.5 py-2.5',
        )}
      >
        {/* Sprint 22 hot-fix — drag handle for column reorder. Hover-
            revealed (matches Sprint 13b sidebar pattern) so it doesn't
            clutter the header in the steady state. Listeners attached
            here only — header/title/badge clicks don't initiate drag. */}
        {reorderEnabled && !collapsed ? (
          <button
            type="button"
            aria-label={`Reorder ${stage.label}`}
            className="-ml-1 inline-flex h-5 w-4 cursor-grab items-center justify-center rounded-xs border-none bg-transparent text-tertiary opacity-0 transition-opacity duration-fast hover:text-primary group-hover/col:opacity-100 active:cursor-grabbing"
            {...sortable.listeners}
          >
            <GripVertical size={12} />
          </button>
        ) : null}
        {collapsed ? (
          <>
            <span className="text-role-badge-count rounded-full bg-muted px-2 py-0.5 text-center text-secondary">
              {count}
            </span>
            <span
              className="text-sm font-semibold text-primary"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {stage.label}
            </span>
          </>
        ) : (
          <>
            <span className="flex-1 truncate text-sm font-semibold text-primary">
              {stage.label}
            </span>
            <span className="min-w-5 text-role-badge-count rounded-full bg-muted px-2 py-0.5 text-center text-secondary">
              {count}
            </span>
          </>
        )}
        {onToggle ? (
          <button
            type="button"
            aria-label={collapsed ? `Expand ${stage.label}` : `Collapse ${stage.label}`}
            onClick={onToggle}
            className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
          >
            <MoreHorizontal size={12} />
          </button>
        ) : null}
      </header>
      {!collapsed ? (
        <div
          ref={setDroppableRef}
          className="flex flex-1 flex-col gap-2 overflow-y-auto p-2"
          data-stage={stage.id}
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}

function Card<T extends KanbanItem>({
  item,
  children,
  selected,
  onSelect,
}: {
  item: T
  children: ReactNode
  selected: boolean
  onSelect?: (item: T) => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.id,
  })
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-card-id={item.id}
      data-selected={selected || undefined}
      onClick={onSelect ? () => onSelect(item) : undefined}
      className={cn(
        'flex cursor-pointer flex-col gap-1.5 rounded-sm border border-border-subtle bg-surface p-2.5 transition-[border-color,box-shadow] duration-fast',
        'hover:border-border-default hover:shadow-sm',
        selected && 'border-accent shadow-[0_0_0_1px_var(--color-accent)]',
        isDragging && 'opacity-50',
      )}
    >
      {children}
    </div>
  )
}
